import { useMemo, useState } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnFiltersState,
} from "@tanstack/react-table"
import { Edit, Play, X } from "lucide-react"
import { NavLink } from "react-router"
import { toast } from "sonner"

import { queryClient } from "@/App.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Dialog, DialogTrigger } from "@/components/ui/dialog.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import { hasRole } from "@/util/hasRole.ts"

import { deleteReport } from "../../api/report/deleteReport.ts"
import { getAllReports } from "../../api/report/getAllReports.ts"

import ReportRunDialog from "./ReportRunDialog.tsx"

import type { DataPointReport } from "../../types/reportTypes.ts"

const columnHelper = createColumnHelper<DataPointReport>()

export default function DataPointReportList() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["allDataPointReports"],
    queryFn: () => getAllReports(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteReport(id),
    onSuccess: () => {
      toast("Report deleted")
      queryClient.invalidateQueries({ queryKey: ["allDataPointReports"] })
    },
  })

  const columnDefs = useMemo(
    () => [
      columnHelper.accessor((r) => r.name, {
        id: "name",
        header: ({ column }) => (
          <div className="p-4 w-[400px] sticky top-0">
            <Input
              className="bg-white"
              placeholder="Search reports"
              value={column.getFilterValue() as string}
              onChange={(e) => column.setFilterValue(e.target.value)}
              autoFocus
            />
          </div>
        ),
        filterFn: "includesString",
        cell: (context) => <div className="p-4 w-[400px]">{context.getValue()}</div>,
      }),
      columnHelper.display({
        id: "Run",

        header: () => <div className="p-4 text-center sticky top-0">Run Report</div>,
        cell: ({ row }) => (
          <div className="text-center">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" disabled={deleteMutation.isPending}>
                  <Play />
                </Button>
              </DialogTrigger>
              <ReportRunDialog report={row.original} />
            </Dialog>
          </div>
        ),
      }),
      columnHelper.display({
        id: "edit",

        header: () => <div className="p-4 text-center sticky top-0">Edit</div>,
        cell: ({ row }) => (
          <div className="text-center">
            <NavLink to={`/datapoints/reports/${row.original.id}`}>
              <Button variant="ghost" disabled={deleteMutation.isPending}>
                <Edit />
              </Button>
            </NavLink>
          </div>
        ),
      }),
      columnHelper.display({
        id: "delete",
        header: () => <div className="p-4 text-center sticky top-0">Delete</div>,
        cell: ({ row }) => (
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={() => row.original.id && deleteMutation.mutate(row.original.id)}
              disabled={deleteMutation.isPending}
            >
              <X />
            </Button>
          </div>
        ),
      }),
    ],
    [deleteMutation],
  )

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: "name", value: "" },
  ])

  const table = useReactTable<DataPointReport>({
    data: data || [],
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
      columnVisibility: {
        edit: hasRole("Report.Admin"),
        delete: hasRole("Report.Admin"),
      },
    },
  })

  if (isPending)
    return (
      <div className="h-[300px] w-full p-4">
        <Skeleton className="h-full w-full" />
      </div>
    )
  if (isError) return <div className="h-[300px] w-full p-4">Network Error</div>

  return (
    <Table className="m-h-full overflow-y-hidden">
      {table.getHeaderGroups().map((hg) => (
        <TableHeader className="bg-card-header border-b sticky top-0" key={hg.id}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <TableHead key={i}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {table.getRowModel()?.rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, i) => (
              <TableCell key={i}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
